import type { ReactNode } from "react";
import { cn } from "@/lib/utils";
import { MONO } from "@/components/home-a/theme";
import type { DemoKind } from "./demoKinds";

/**
 * Small animated previews of each sample report, built from the same demo
 * captures and the same measured numbers as the fixtures. They sit in the
 * middle of text-heavy pages so a reader sees what the report shows before
 * reading about it; clicking one opens the full sample.
 *
 * Numbers here are copied from the fixtures (see scripts/demo/build-fixtures.mjs
 * and src/lib/__tests__/demoFixtures.test.tsx, which pins them).
 */

type Tr = (el: string, en: string) => string;

const KEYFRAMES = `
@keyframes ecv-pop { 0%, 8% { opacity: 0; transform: scale(.96) } 14%, 86% { opacity: 1; transform: scale(1) } 94%, 100% { opacity: 0; transform: scale(.98) } }
@keyframes ecv-fade { 0%, 40% { opacity: 0 } 50%, 90% { opacity: 1 } 100% { opacity: 0 } }
@keyframes ecv-grow { 0%, 12% { transform: scaleX(0) } 40%, 88% { transform: scaleX(1) } 100% { transform: scaleX(1) } }
@keyframes ecv-dot { 0%, 20% { opacity: 0; transform: scale(.4) } 30%, 90% { opacity: 1; transform: scale(1) } 100% { opacity: 0 } }
@keyframes ecv-pulse { 0%, 100% { box-shadow: 0 0 0 0 rgba(239, 68, 68, .45) } 50% { box-shadow: 0 0 0 6px rgba(239, 68, 68, 0) } }
.ecv-pop { animation: ecv-pop 9s ease-in-out infinite both; }
.ecv-fade { animation: ecv-fade 7s ease-in-out infinite both; }
.ecv-grow { transform-origin: left; animation: ecv-grow 6s cubic-bezier(.2,.7,.2,1) infinite both; }
.ecv-dot { animation: ecv-dot 6s ease-out infinite both; }
.ecv-pulse { animation: ecv-pulse 2.4s ease-in-out infinite; }
@media (prefers-reduced-motion: reduce) { .ecv-pop, .ecv-fade, .ecv-grow, .ecv-dot, .ecv-pulse { animation: none; opacity: 1; transform: none; } }
`;

/** Browser chrome around a first-screen capture (1440 × 900). */
function Frame({ src, alt, children, host }: { src: string; alt: string; children?: ReactNode; host: string }) {
  return (
    <div className="overflow-hidden rounded-[14px] border border-[hsl(var(--ec-line-strong))] bg-card shadow-[0_30px_60px_-30px_rgba(0,0,0,0.9)]">
      <div className="flex items-center gap-1.5 border-b border-border px-3 py-2">
        <span className="h-2 w-2 rounded-full bg-white/15" />
        <span className="h-2 w-2 rounded-full bg-white/15" />
        <span className="h-2 w-2 rounded-full bg-white/15" />
        <span className="ml-3 truncate rounded-md bg-foreground/[0.04] px-2 py-0.5 text-[10.5px] text-[hsl(var(--ec-muted))]" style={{ fontFamily: MONO }}>{host}</span>
      </div>
      <div className="relative aspect-[1440/900] overflow-hidden bg-white">
        <img src={src} alt={alt} loading="lazy" decoding="async" className="absolute inset-0 h-full w-full object-cover object-top" />
        {children}
      </div>
    </div>
  );
}

/** A marker box in % of the first screen, with a numbered label. */
function Mark({ n, x, y, w, h, label, delay, tone = "bad" }: { n: number; x: number; y: number; w: number; h: number; label: string; delay: number; tone?: "bad" | "warn" | "good" }) {
  const c = tone === "good" ? "hsl(var(--ec-mint))" : tone === "warn" ? "hsl(var(--warning))" : "hsl(var(--destructive))";
  return (
    <div className="ecv-pop absolute" style={{ left: `${x}%`, top: `${y}%`, width: `${w}%`, height: `${h}%`, animationDelay: `${delay}s` }}>
      <div className="absolute inset-0 rounded-[4px]" style={{ border: `2px solid ${c}`, background: `color-mix(in srgb, ${c} 10%, transparent)` }} />
      <div className="absolute -top-[26px] left-0 flex max-w-[260px] items-center gap-1.5 whitespace-nowrap rounded-full px-2 py-[3px] text-[10.5px] font-semibold text-white shadow-lg" style={{ background: c }}>
        <span className="grid h-4 w-4 place-items-center rounded-full bg-black/25 text-[9.5px]">{n}</span>
        {label}
      </div>
    </div>
  );
}

function Chip({ children, className, delay = 0 }: { children: ReactNode; className?: string; delay?: number }) {
  return (
    <div className={cn("ecv-pop absolute rounded-[12px] border border-[hsl(var(--ec-line-strong))] bg-background/95 px-3 py-2 text-[11.5px] text-foreground shadow-xl backdrop-blur", className)} style={{ animationDelay: `${delay}s` }}>
      {children}
    </div>
  );
}

function CroVignette({ tr }: { tr: Tr }) {
  return (
    <Frame src="/demo/kyma-desktop-vp.jpg" alt={tr("Σελίδα προϊόντος του demo καταστήματος KYMA", "Product page of the KYMA demo store")} host="kyma.example/products/relaxed-cotton-tee">
      {/* Positions: demo geometry (kyma-desktop), first 900 px of a 2715 px page. */}
      <Mark n={1} x={56.9} y={53.4} w={31.9} h={6.2} label={tr("Κουμπί σαν ανενεργό · 1.6:1", "Button reads as disabled · 1.6:1")} delay={0} />
      <Mark n={2} x={56.9} y={65.3} w={31.9} h={16.6} label={tr("Παράδοση κρυμμένη", "Delivery folded away")} delay={1.2} tone="warn" />
      <Mark n={3} x={56.9} y={37.5} w={12} h={3.2} label={tr("Swatches > κουμπί", "Swatches outrank the button")} delay={2.4} tone="warn" />
      <Chip className="bottom-3 left-3" delay={3.2}>
        <span className="text-[hsl(var(--ec-muted))]">CRO score</span> <b className="ml-1 text-[15px] tabular-nums">58</b><span className="text-[hsl(var(--ec-muted))]">/100</span>
      </Chip>
    </Frame>
  );
}

function LeadVignette({ tr }: { tr: Tr }) {
  return (
    <Frame src="/demo/northstar-desktop-vp.jpg" alt={tr("Σελίδα με φόρμα του demo γραφείου Northstar", "Lead page of the Northstar demo firm")} host="northstar.example/accounting">
      <Mark n={1} x={58.3} y={15} w={30.6} h={70.8} label={tr("9 πεδία, 8 υποχρεωτικά", "9 fields, 8 required")} delay={0} tone="warn" />
      <Mark n={2} x={60.2} y={74.9} w={26.8} h={5.3} label={tr('"Submit" σε 2.4:1', '"Submit" at 2.4:1')} delay={1.4} />
      <Chip className="bottom-3 left-3" delay={2.6}>
        {tr("Πρόταση", "Suggested")}: <b>Book my free consultation</b>
      </Chip>
    </Frame>
  );
}

function HeatVignette({ tr }: { tr: Tr }) {
  return (
    <Frame src="/demo/kyma-desktop-vp.jpg" alt={tr("Heatmap της σελίδας προϊόντος KYMA", "Heatmap of the KYMA product page")} host="kyma.example/products/relaxed-cotton-tee">
      {/* The composite is the whole page at the same aspect; object-top shows the same first screen. */}
      <div className="absolute inset-0 overflow-hidden">
        <img src="/demo/kyma-desktop-composite.jpg" alt="" loading="lazy" decoding="async" className="ecv-fade absolute left-0 top-0 w-full" style={{ height: "auto" }} />
      </div>
      <Chip className="left-3 top-3" delay={0.6}>
        {tr("Φωτογραφία", "Photo")} <b className="ml-1 tabular-nums">48.3%</b>
      </Chip>
      <Chip className="bottom-3 right-3" delay={1.6}>
        Add to bag <b className="ml-1 tabular-nums text-[hsl(var(--destructive))]">0.6%</b>
      </Chip>
    </Frame>
  );
}

function ContentVignette({ tr }: { tr: Tr }) {
  const bars: Array<[string, number, number]> = [["Logos", 40, 50], ["Pathos", 20, 20], ["Ethos", 40, 30]];
  return (
    <div className="relative">
      <Frame src="/demo/northstar-desktop-vp.jpg" alt={tr("Σελίδα υπηρεσίας Northstar", "Northstar service page")} host="northstar.example/accounting">
        <Mark n={1} x={11.1} y={15} w={43.9} h={10.7} label={tr("Τίτλος: κατηγορία, όχι αποτέλεσμα", "Headline: a category, not an outcome")} delay={0} tone="warn" />
        <Mark n={2} x={60.2} y={74.9} w={26.8} h={5.3} label={tr('"Submit" χωρίς αξία', '"Submit" names no value')} delay={1.3} />
      </Frame>
      <div className="ecv-pop absolute -bottom-6 right-3 w-[210px] rounded-[12px] border border-[hsl(var(--ec-line-strong))] bg-background/95 p-3 shadow-xl" style={{ animationDelay: "2.2s" }}>
        <p className="mb-2 text-[10px] uppercase tracking-[0.12em] text-[hsl(var(--ec-muted))]" style={{ fontFamily: MONO }}>{tr("Μείγμα πειθούς", "Persuasion mix")}</p>
        {bars.map(([k, v, target], i) => (
          <div key={k} className="mb-1.5 grid grid-cols-[48px_minmax(0,1fr)_28px] items-center gap-2 text-[10.5px]">
            <span className="text-[hsl(var(--ec-text-2))]">{k}</span>
            <span className="relative h-1.5 rounded-full bg-foreground/[0.07]">
              <span className="ecv-grow absolute inset-y-0 left-0 rounded-full bg-[hsl(var(--ec-mint))]" style={{ width: `${v}%`, animationDelay: `${2.4 + i * 0.2}s` }} />
              <span className="absolute -top-0.5 h-2.5 w-px bg-white/60" style={{ left: `${target}%` }} />
            </span>
            <span className="text-right tabular-nums">{v}%</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function TechVignette({ tr }: { tr: Tr }) {
  // Stages and beacons as the technical capture of the KYMA demo recorded them.
  const stages = [tr("Πριν την επιλογή", "Before any choice"), "Deny", "Allow all"];
  const rows: Array<{ name: string; fires: boolean[] }> = [
    { name: "Google Analytics 4", fires: [false, false, true] },
    { name: "Meta Pixel", fires: [true, true, true] },
  ];
  return (
    <div className="overflow-hidden rounded-[14px] border border-[hsl(var(--ec-line-strong))] bg-card p-4 shadow-[0_30px_60px_-30px_rgba(0,0,0,0.9)] sm:p-5">
      <div className="flex items-center justify-between">
        <p className="text-[11px] uppercase tracking-[0.12em] text-[hsl(var(--ec-muted))]" style={{ fontFamily: MONO }}>{tr("Tracking ανά στάδιο συγκατάθεσης", "Tracking by consent stage")}</p>
        <span className="rounded-full border border-border px-2 py-0.5 text-[10.5px] text-[hsl(var(--ec-text-2))]" style={{ fontFamily: MONO }}>kyma.example</span>
      </div>
      <div className="mt-4 grid grid-cols-[130px_repeat(3,minmax(0,1fr))] gap-y-3 text-[12px]">
        <span />
        {stages.map((s) => <span key={s} className="text-center text-[10.5px] text-[hsl(var(--ec-muted))]">{s}</span>)}
        {rows.map((r, ri) => (
          <div key={r.name} className="contents">
            <span className="self-center text-foreground">{r.name}</span>
            {r.fires.map((f, si) => {
              const bad = f && si < 2;
              return (
                <span key={si} className="grid place-items-center">
                  <span className={cn("ecv-dot grid h-6 w-6 place-items-center rounded-full text-[11px] font-bold", !f ? "bg-foreground/[0.06] text-[hsl(var(--ec-muted))]" : bad ? "ecv-pulse bg-[hsl(var(--destructive))] text-white" : "bg-[hsl(var(--ec-mint))] text-background")} style={{ animationDelay: `${ri * 0.3 + si * 0.5}s` }}>
                    {f ? "●" : "–"}
                  </span>
                </span>
              );
            })}
          </div>
        ))}
      </div>
      <div className="mt-5 grid grid-cols-3 gap-2">
        {[["LCP mobile", "3.6 s", "bad"], ["Hero JPEG", "1,040 KB", "warn"], ["JS error", "size-guide.js", "warn"]].map(([k, v, t], i) => (
          <div key={k} className="ecv-pop rounded-[10px] border border-border bg-background/60 px-2.5 py-2" style={{ animationDelay: `${1.8 + i * 0.4}s` }}>
            <p className="text-[10px] text-[hsl(var(--ec-muted))]">{k}</p>
            <p className={cn("mt-0.5 truncate text-[13px] font-semibold tabular-nums", t === "bad" ? "text-[hsl(var(--destructive))]" : "text-[hsl(var(--warning))]")}>{v}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

function CompetitorVignette({ tr }: { tr: Tr }) {
  const pages: Array<[string, string, number, boolean]> = [["KYMA", "/demo/kyma-desktop-vp.jpg", 0.6, true], ["LINO", "/demo/lino-desktop-vp.jpg", 5.8, false], ["AURA", "/demo/aura-desktop-vp.jpg", 8.2, false]];
  return (
    <div className="rounded-[14px] border border-[hsl(var(--ec-line-strong))] bg-card p-3 shadow-[0_30px_60px_-30px_rgba(0,0,0,0.9)] sm:p-4">
      <p className="mb-3 text-[11px] uppercase tracking-[0.12em] text-[hsl(var(--ec-muted))]" style={{ fontFamily: MONO }}>{tr("Προσοχή στο κουμπί αγοράς", "Attention on the buy button")}</p>
      <div className="grid grid-cols-3 gap-2.5">
        {pages.map(([name, src, share, you], i) => (
          <div key={name}>
            <div className={cn("relative aspect-[1440/900] overflow-hidden rounded-[8px] border bg-white", you ? "border-[hsl(var(--ec-mint)/0.6)]" : "border-border")}>
              <img src={src} alt={name} loading="lazy" decoding="async" className="absolute inset-0 h-full w-full object-cover object-top" />
            </div>
            <div className="mt-2 flex items-center justify-between text-[11.5px]">
              <span className="font-semibold">{name}{you && <span className="ml-1 text-[10px] font-normal text-[hsl(var(--ec-mint))]">{tr("εσείς", "you")}</span>}</span>
              <span className="tabular-nums text-[hsl(var(--ec-text-2))]">{share}%</span>
            </div>
            <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-foreground/[0.06]">
              <div className={cn("ecv-grow h-full rounded-full", you ? "bg-[hsl(var(--destructive))]" : "bg-[hsl(var(--ec-mint))]")} style={{ width: `${(share / 8.2) * 100}%`, animationDelay: `${i * 0.25}s` }} />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function VoltaVignette({ tr }: { tr: Tr }) {
  return (
    <Frame src="/demo/volta-desktop-vp.jpg" alt={tr("Σελίδα προϊόντος του demo καταστήματος VOLTA", "Product page of the VOLTA demo store")} host="volta.example/headphones/aria-anc">
      {/* Positions: demo geometry (volta-desktop), first 900 px. */}
      <Mark n={1} x={57.6} y={57.1} w={32.6} h={13.9} label={tr("PayPal + ανταλλαγή: 22.6% vs 16%", "PayPal + trade-in: 22.6% vs 16%")} delay={0} />
      <Mark n={2} x={57.6} y={77.4} w={32.6} h={19.5} label={tr("Οφέλη γραμμένα ως specs", "Benefits written as specs")} delay={1.2} tone="warn" />
      <Chip className="bottom-3 left-3" delay={2.4}>
        Add to cart <b className="ml-1 text-[hsl(var(--ec-mint))]">{tr("1ο · 5.2:1", "1st · 5.2:1")}</b>
      </Chip>
    </Frame>
  );
}

function SaasVignette({ tr }: { tr: Tr }) {
  return (
    <Frame src="/demo/loopdesk-desktop-vp.jpg" alt={tr("Σελίδα τιμών του demo SaaS Loopdesk", "Pricing page of the Loopdesk demo SaaS")} host="loopdesk.example/pricing">
      <Mark n={1} x={12.7} y={57.5} w={74.6} h={8.2} label={tr("4 ίδια κουμπιά, κανένα προτεινόμενο", "4 equal buttons, none recommended")} delay={0} />
      <Mark n={2} x={11.1} y={84} w={77.8} h={3.4} label={tr('"Billed annually" σε γκρι 12.5 px', '"Billed annually" in 12.5 px grey')} delay={1.2} tone="warn" />
      <Chip className="left-3 top-3" delay={2.4}>
        {tr("14 ημέρες δωρεάν, χωρίς κάρτα:", "14 days free, no card:")} <b className="ml-1 text-[hsl(var(--warning))]">{tr("στο 73% και 84%", "at 73% and 84%")}</b>
      </Chip>
    </Frame>
  );
}

function BookingVignette({ tr }: { tr: Tr }) {
  return (
    <Frame src="/demo/aster-desktop-vp.jpg" alt={tr("Σελίδα θεραπείας της demo κλινικής Aster Physio", "Treatment page of the Aster Physio demo clinic")} host="asterphysio.example/treatments">
      <Mark n={1} x={60.8} y={58} w={26.4} h={9.8} label={tr("Όλες οι ώρες μοιάζουν πιασμένες", "Every time looks taken")} delay={0} tone="warn" />
      <Mark n={2} x={60.8} y={69.3} w={26.4} h={4.2} label={tr('Τιμή "εξαρτάται" · €60 μόνο στο FAQ', 'Price "depends" · €60 only in the FAQ')} delay={1.2} />
      <Mark n={3} x={60.8} y={75.2} w={26.4} h={6} label={tr("Κουμπί σε 2.2:1", "Button at 2.2:1")} delay={2.4} />
      <Chip className="bottom-3 left-3" delay={3.2}>
        4.9 · 312 {tr("κριτικές", "reviews")} <b className="ml-1 text-[hsl(var(--ec-mint))]">✓</b>
      </Chip>
    </Frame>
  );
}

function InfoVignette({ tr }: { tr: Tr }) {
  return (
    <Frame src="/demo/plotline-desktop-vp.jpg" alt={tr("Άρθρο του demo περιοδικού Plotline", "Article on the Plotline demo magazine")} host="plotline.example/grow">
      <Mark n={1} x={16.3} y={10.3} w={67.4} h={14} label={tr("Διαφήμιση: 17.4% της προσοχής", "Ad: 17.4% of attention")} delay={0} />
      <Mark n={2} x={11.8} y={31.3} w={48.6} h={16.6} label={tr("Τίτλος: 4.6%", "Title: 4.6%")} delay={1.2} tone="warn" />
      <Chip className="bottom-3 right-3" delay={2.4}>
        {tr("Newsletter:", "Newsletter:")} <b className="ml-1 text-[hsl(var(--destructive))]">{tr("μόνο στο 82%", "only at 82%")}</b>
      </Chip>
    </Frame>
  );
}

function HeatVoltaVignette({ tr }: { tr: Tr }) {
  return (
    <Frame src="/demo/volta-desktop-vp.jpg" alt={tr("Heatmap της σελίδας προϊόντος VOLTA", "Heatmap of the VOLTA product page")} host="volta.example/headphones/aria-anc">
      <div className="absolute inset-0 overflow-hidden">
        <img src="/demo/volta-desktop-composite.jpg" alt="" loading="lazy" decoding="async" className="ecv-fade absolute left-0 top-0 w-full" style={{ height: "auto" }} />
      </div>
      <Chip className="left-3 top-3" delay={0.6}>
        Add to cart <b className="ml-1 tabular-nums text-[hsl(var(--ec-mint))]">16%</b>
      </Chip>
      <Chip className="bottom-3 right-3" delay={1.6}>
        PayPal + trade-in <b className="ml-1 tabular-nums text-[hsl(var(--destructive))]">22.6%</b>
      </Chip>
    </Frame>
  );
}

/** Five pages of the VOLTA path, each with the report's score for it. */
function FunnelVignette({ tr }: { tr: Tr }) {
  const pages: Array<[string, string, number]> = [
    [tr("Αρχική", "Home"), "volta-home", 84], [tr("Κατηγορία", "Category"), "volta-category", 76], [tr("Προϊόν", "Product"), "volta-product", 66],
    [tr("Καλάθι", "Cart"), "volta-cart", 62], ["Checkout", "volta-checkout", 38],
  ];
  return (
    <div className="rounded-[14px] border border-[hsl(var(--ec-line-strong))] bg-card p-3 shadow-[0_30px_60px_-30px_rgba(0,0,0,0.9)] sm:p-4">
      <p className="mb-3 text-[11px] uppercase tracking-[0.12em] text-[hsl(var(--ec-muted))]" style={{ fontFamily: MONO }}>{tr("Η διαδρομή, σελίδα προς σελίδα", "The path, page by page")}</p>
      <div className="grid grid-cols-5 gap-1.5">
        {pages.map(([name, img, score], i) => {
          const bad = score < 40, mid = score < 70;
          return (
            <div key={img} className="ecv-pop" style={{ animationDelay: `${i * 0.35}s` }}>
              <div className={cn("relative aspect-[1440/900] overflow-hidden rounded-[6px] border bg-white", bad ? "border-[hsl(var(--destructive)/0.7)]" : "border-border")}>
                <img src={`/demo/flow/${img}.jpg`} alt={name} loading="lazy" decoding="async" className="absolute inset-0 h-full w-full object-cover object-top" />
              </div>
              <p className="mt-1.5 truncate text-[10.5px] text-[hsl(var(--ec-text-2))]">{i + 1}. {name}</p>
              <p className={cn("text-[15px] font-semibold tabular-nums", bad ? "text-[hsl(var(--destructive))]" : mid ? "text-[hsl(var(--warning))]" : "text-[hsl(var(--ec-mint))]")}>{score}</p>
            </div>
          );
        })}
      </div>
      <p className="ecv-pop mt-3 rounded-[10px] border border-[hsl(var(--destructive)/0.35)] bg-[hsl(var(--destructive)/0.08)] px-3 py-2 text-[11.5px]" style={{ animationDelay: "2s" }}>
        {tr("Checkout: πρώτα λογαριασμός και express €4.90 προεπιλεγμένη σε δωρεάν αποστολή.", "Checkout: account first, and €4.90 express preselected on a free-delivery order.")}
      </p>
    </div>
  );
}

/** What the AI visitor did on Aster Physio, one screen per step. */
function AgentVignette({ tr }: { tr: Tr }) {
  const steps: Array<[string, string, "ok" | "warn" | "bad"]> = [
    ["aster-home", tr('Πάτησε "Book online"', 'Clicked "Book online"'), "ok"],
    ["aster-slot", tr("Διάλεξε Πέμπτη 09:30", "Picked Thursday 09:30"), "ok"],
    ["aster-faq", tr("Έψαξε την τιμή στο FAQ", "Hunted for the price in the FAQ"), "warn"],
    ["aster-details", tr("Λογαριασμός 8 πεδίων: σταμάτησε", "8-field account: stopped"), "bad"],
  ];
  return (
    <div className="rounded-[14px] border border-[hsl(var(--ec-line-strong))] bg-card p-3 shadow-[0_30px_60px_-30px_rgba(0,0,0,0.9)] sm:p-4">
      <p className="mb-3 text-[11px] uppercase tracking-[0.12em] text-[hsl(var(--ec-muted))]" style={{ fontFamily: MONO }}>{tr("Στόχος: κράτηση πρώτης επίσκεψης", "Goal: book a first visit")}</p>
      <div className="grid grid-cols-2 gap-2.5">
        {steps.map(([img, text, tone], i) => (
          <div key={img} className="ecv-pop" style={{ animationDelay: `${i * 0.5}s` }}>
            <div className="relative aspect-[1440/900] overflow-hidden rounded-[8px] border border-border bg-white">
              <img src={`/demo/flow/${img}.jpg`} alt="" loading="lazy" decoding="async" className="absolute inset-0 h-full w-full object-cover object-top" />
              <span className={cn("absolute left-1.5 top-1.5 grid h-5 w-5 place-items-center rounded-full text-[10px] font-bold text-white", tone === "bad" ? "bg-[hsl(var(--destructive))]" : tone === "warn" ? "bg-[hsl(var(--warning))]" : "bg-[hsl(var(--primary))]")}>{i + 1}</span>
            </div>
            <p className="mt-1.5 text-[11px] leading-snug text-[hsl(var(--ec-text-2))]">{text}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

export function DemoVignette({ kind, tr }: { kind: DemoKind; tr: Tr }) {
  const VIGNETTES: Record<DemoKind, (p: { tr: Tr }) => JSX.Element> = {
    cro: CroVignette, "cro-volta": VoltaVignette, leadgen: LeadVignette, saas: SaasVignette, booking: BookingVignette, information: InfoVignette,
    heatmap: HeatVignette, "heatmap-volta": HeatVoltaVignette, content: ContentVignette, technical: TechVignette, competitor: CompetitorVignette,
    funnel: FunnelVignette, agent: AgentVignette,
  };
  const V = VIGNETTES[kind];
  const body = <V tr={tr} />;
  return (
    <>
      <style>{KEYFRAMES}</style>
      {body}
    </>
  );
}
